import { Button, openModal, toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
  FaxLetterCategory,
  FaxLetterDecision,
  markFaxLetterIncorrect,
  saveFaxLetterReview,
} from "../../api/postCall/faxLetter";
import { LoaderMessage } from "../../components/loaderMessage/loaderMessage";
import { useDataAccumulatorForSingleOrder } from "../../context/dataAccumulatorForSingleOrder";
import { ModalId } from "../../enums/modalId";
import { closeModal, setOpenedModalId } from "../../redux/slice/modalSlice";
import { AppDispatch, RootState } from "../../redux/store/store";
import { CrossIcon } from "../../svg/cross-icon";
import { getOrgIdForFetchExternalWorklist } from "../../utils/organizationHelper";
import { showApiError } from "../../utils/showApiError";
import AuthorisationDocumentViewer from "../authorisationDetails/components/AuthorisationDocumentViewer";
import {
  AUTHORISATION_DETAILS_TITLE,
  CANCEL_BUTTON_TEXT,
  SAVE_BUTTON_TEXT,
} from "../authorisationDetails/utils/constants";
import { faxLetterDetailsPath } from "../worklists/singleOrder/cptCodes/utils/faxLetterDocument";
import AuthorizationCard from "./components/AuthorizationCard";
import DocumentsModal from "./components/DocumentsModal";
import IdentificationCard from "./components/IdentificationCard";
import IncorrectLetterModal from "./components/IncorrectLetterModal";
import SaveReviewModal from "./components/SaveReviewModal";
import { useFaxLetterDetails } from "./hooks/useFaxLetterDetails";
import { useFaxLetterReview } from "./hooks/useFaxLetterReview";
import {
  DEFAULT_LETTER_TITLE,
  DETAILS_LOAD_ERROR_MESSAGE,
  DETAILS_NOT_FOUND_MESSAGE,
  LETTER_MARKED_INCORRECT_MESSAGE,
  LETTER_MARK_INCORRECT_FAILED_MESSAGE,
  LETTER_REMOVED_FROM_JCODE_MESSAGE,
  LOADING_DETAILS_MESSAGE,
  REVIEW_SAVED_AND_UPLOADED_MESSAGE,
  REVIEW_SAVED_MESSAGE,
  REVIEW_SAVE_FAILED_MESSAGE,
} from "./utils/constants";
import { buildFaxLetterDocumentName } from "./utils/faxLetterCategory";

const PAGE_MODALS: string[] = [
  ModalId.FAX_LETTER_SAVE_MODAL,
  ModalId.FAX_LETTER_INCORRECT_MODAL,
  ModalId.FAX_LETTER_DOCUMENTS_MODAL,
];

/**
 * Authorization Details for a payer fax that fax intake attached to a pending J-code, for review.
 *
 * Left: the letter with Incorrect Letter, Download, and Documents. Right: Identification and
 * an Authorization section laid out for the letter's category, with Approve / Reject (and edits on
 * Reject). Both save actions apply reviewed authorization values to the drug. Approval, Denial, and
 * NAR offer "Upload to Documents"; other categories save directly. Either marks the letter reviewed.
 */
const FaxLetterDetails = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { resetAll } = useDataAccumulatorForSingleOrder();
  const { openedModalId } = useSelector((s: RootState) => s.modal);
  const { authId, jCodeId, documentId } = useParams<{
    authId?: string;
    jCodeId?: string;
    documentId?: string;
  }>();
  const orgId = getOrgIdForFetchExternalWorklist();
  const letter = {
    order_id: authId ?? "",
    org_id: orgId,
    j_code_id: jCodeId ?? "",
    document_id: documentId ?? "",
  };
  const { status, details, pdfUrl, pdfLoading } = useFaxLetterDetails(
    letter.order_id,
    letter.org_id,
    letter.j_code_id,
    letter.document_id,
  );
  const review = useFaxLetterReview(details);
  const [isSaving, setIsSaving] = useState(false);
  const [isMarking, setIsMarking] = useState(false);

  useEffect(() => {
    if (openedModalId && PAGE_MODALS.includes(openedModalId)) {
      openModal(openedModalId);
    }
  }, [openedModalId]);

  const handleClose = () => navigate(-1);
  const openPageModal = (id: ModalId) => dispatch(setOpenedModalId({ id }));
  const closePageModal = () => dispatch(closeModal());
  const handleSwitchDocument = (nextDocumentId: string) => {
    review.reset();
    closePageModal();
    navigate(
      faxLetterDetailsPath(letter.order_id, letter.j_code_id, nextDocumentId),
      { replace: true },
    );
  };

  const handleSubmitReview = async (documentName: string, upload: boolean) => {
    setIsSaving(true);
    try {
      await saveFaxLetterReview({
        ...letter,
        decision:
          review.decision === "valid"
            ? FaxLetterDecision.Approved
            : FaxLetterDecision.Rejected,
        corrections: review.decision === "invalid" ? review.corrections() : {},
        additional_comments:
          review.values.category === FaxLetterCategory.Denial
            ? review.additionalComments.trim() || null
            : undefined,
        document_name: documentName,
        upload,
      });
      // The returning Drugs tab reuses this cache unless the committed review invalidates it.
      resetAll();
      toast.success(
        upload ? REVIEW_SAVED_AND_UPLOADED_MESSAGE : REVIEW_SAVED_MESSAGE,
      );
      closePageModal();
      handleClose();
    } catch (error) {
      showApiError(error, REVIEW_SAVE_FAILED_MESSAGE);
    } finally {
      setIsSaving(false);
    }
  };

  const handleMarkIncorrect = async () => {
    setIsMarking(true);
    try {
      const { fax_unmatched } = await markFaxLetterIncorrect(letter);
      toast.success(
        fax_unmatched
          ? LETTER_MARKED_INCORRECT_MESSAGE
          : LETTER_REMOVED_FROM_JCODE_MESSAGE,
      );
      closePageModal();
      handleClose();
    } catch (error) {
      showApiError(error, LETTER_MARK_INCORRECT_FAILED_MESSAGE);
    } finally {
      setIsMarking(false);
    }
  };

  const defaultDocumentName =
    details?.review?.document_name ||
    buildFaxLetterDocumentName({
      patientName: details?.identification.patient_name,
      payer: details?.identification.payer,
      jCode: details?.identification.j_code,
      drugName: details?.identification.medicine_name,
      category: review.values.category,
      dateOfService: details?.identification.date_of_service,
      receivedDate: details?.received_date,
    });
  const isReady = status === "ready" && !!details;
  const handleSave = () => {
    if (
      [
        FaxLetterCategory.Approval,
        FaxLetterCategory.Denial,
        FaxLetterCategory.NoAuthRequired,
      ].includes(review.values.category as FaxLetterCategory)
    ) {
      openPageModal(ModalId.FAX_LETTER_SAVE_MODAL);
    } else {
      void handleSubmitReview(defaultDocumentName, false);
    }
  };

  return (
    <div className="flex h-screen flex-col bg-white">
      <header className="flex h-14 shrink-0 items-center justify-between border-b border-primaryGray-15 px-5 py-0">
        <h1 className="text-base font-bold leading-tight text-[#0F0F0F]">
          {AUTHORISATION_DETAILS_TITLE}
        </h1>
        <button
          type="button"
          onClick={handleClose}
          className="flex w-5 flex-shrink-0 flex-col items-start p-1 pb-0"
          aria-label="Close"
        >
          <CrossIcon width="20" height="20" />
        </button>
      </header>

      <div className="flex min-h-0 flex-1 overflow-hidden">
        <AuthorisationDocumentViewer
          pdfUrl={pdfUrl}
          documentFileName={details?.document.doc_title ?? DEFAULT_LETTER_TITLE}
          isLoading={status === "loading" || pdfLoading}
          onIncorrectLetterClick={
            isReady
              ? () => openPageModal(ModalId.FAX_LETTER_INCORRECT_MODAL)
              : undefined
          }
          showDocuments={!!details?.previous_documents.length}
          onDocumentsClick={() =>
            openPageModal(ModalId.FAX_LETTER_DOCUMENTS_MODAL)
          }
        />

        <div className="relative flex min-h-0 flex-1 flex-col overflow-y-auto p-6 pb-10">
          {status === "loading" && (
            <div
              className="flex flex-1 items-center justify-center"
              aria-busy="true"
              aria-live="polite"
            >
              <LoaderMessage message={LOADING_DETAILS_MESSAGE} />
            </div>
          )}
          {(status === "not_found" || status === "error") && (
            <div
              className="flex flex-1 flex-col items-center justify-center gap-2 p-6 text-center text-sm text-red-600"
              role="alert"
            >
              {status === "not_found"
                ? DETAILS_NOT_FOUND_MESSAGE
                : DETAILS_LOAD_ERROR_MESSAGE}
            </div>
          )}
          {isReady && (
            <div className="flex shrink-0 flex-col gap-4">
              <IdentificationCard identification={details.identification} />
              <AuthorizationCard
                values={review.values}
                changedFields={review.changedFields}
                decision={review.decision}
                onDecisionChange={review.setDecision}
                onFieldChange={review.setField}
                additionalComments={review.additionalComments}
                onAdditionalCommentsChange={review.setAdditionalComments}
                validationErrors={review.validationErrors}
              />
            </div>
          )}
        </div>
      </div>

      <footer className="flex shrink-0 justify-end border-t border-primaryGray-15 bg-white px-6 py-4">
        <div className="flex w-[60%] min-w-0 gap-3">
          <Button
            buttonType="secondary"
            size="medium"
            onClick={review.reset}
            className="flex-1"
            children={CANCEL_BUTTON_TEXT}
            disabled={!isReady}
          />
          <Button
            buttonType="primary"
            size="medium"
            onClick={handleSave}
            className="flex-1"
            children={SAVE_BUTTON_TEXT}
            disabled={!isReady || !review.canSave || isSaving}
          />
        </div>
      </footer>

      {openedModalId === ModalId.FAX_LETTER_SAVE_MODAL && (
        <SaveReviewModal
          defaultDocumentName={defaultDocumentName}
          isSaving={isSaving}
          onSubmit={handleSubmitReview}
          onClose={closePageModal}
        />
      )}
      {openedModalId === ModalId.FAX_LETTER_INCORRECT_MODAL && (
        <IncorrectLetterModal
          jCode={details?.identification.j_code}
          isSubmitting={isMarking}
          onConfirm={handleMarkIncorrect}
          onClose={closePageModal}
        />
      )}
      {openedModalId === ModalId.FAX_LETTER_DOCUMENTS_MODAL && details && (
        <DocumentsModal
          documents={details.previous_documents}
          currentDocumentId={letter.document_id}
          onSwitchDocument={handleSwitchDocument}
          onClose={closePageModal}
        />
      )}
    </div>
  );
};

export default FaxLetterDetails;
